import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { Forecast } from '@futrli-sdk/predict';
import { matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ModifyConsolidatedForecastView } from '@futrli/views/views/forecasts/ModifyConsolidatedForecastView/ModifyConsolidatedForecastView';
import { ModifyForecastView } from '@futrli/views/views/forecasts/ModifyForecastView/ModifyForecastView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { usePredictionsInfoContext } from '@futrli/predict/forecast/detail/context/predictions-info-context';
import { useRouteIds } from '@futrli/predict/hooks/useRouteIds';

interface PublishForecastDialogProps {
  forecastId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const PublishForecastDialog = ({ forecastId, isOpen, onClose }: PublishForecastDialogProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId } = useRouteIds();
  const { syncForecast } = usePredictionsInfoContext();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const ViewClass = isConsolidation ? ModifyConsolidatedForecastView : ModifyForecastView;
  const view = useApiView(
    useMemo(() => new ViewClass(workspaceId, organisationId, forecastId), [
      ViewClass,
      workspaceId,
      organisationId,
      forecastId
    ])
  );

  // Need to keep this in useState so that the success text does not update after the publish/unpublish action has completed, as the forecast status will have updated in the view
  const [isPublishing] = useState(view.state.forecast?.status === 'draft');

  const handlePublish = useCallback(() => {
    const updateRequest = isPublishing ? view.publishForecast() : view.unpublishForecast();

    void updateRequest
      .then(updatedForecast => {
        if (!isConsolidation && updatedForecast && syncForecast) {
          syncForecast(updatedForecast as Forecast);
        }
      })
      .catch(() => undefined);
  }, [isConsolidation, isPublishing, syncForecast, view]);

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isUpdating,
    error: view.state.errorUpdating,
    onAction: handlePublish,
    onClose
  });

  const text = useMemo(() => {
    if (isPublishing) {
      return {
        title: intl.formatMessage({ id: 'predict.forecasting.publish', defaultMessage: 'Publish' }),
        description: intl.formatMessage({
          id: 'predict.forecasting.publish_forecast_description',
          defaultMessage: 'Publishing this forecast will make it available for all users to see'
        }),
        loadingMessage: intl.formatMessage({
          id: 'predict.forecasting.publishing_forecast',
          defaultMessage: 'Publishing forecast...'
        }),
        successMessage: intl.formatMessage({
          id: 'predict.forecasting.forecast_published',
          defaultMessage: 'Forecast published'
        })
      };
    }
    return {
      title: intl.formatMessage({ id: 'predict.forecasting.unpublish', defaultMessage: 'Unpublish' }),
      description: intl.formatMessage({
        id: 'predict.forecasting.unpublish_forecast_description',
        defaultMessage: 'Unpublishing this forecast will put it back into a draft state'
      }),
      loadingMessage: intl.formatMessage({
        id: 'predict.forecasting.unpublishing_forecast',
        defaultMessage: 'Unpublishing forecast...'
      }),
      successMessage: intl.formatMessage({
        id: 'predict.forecasting.forecast_unpublished',
        defaultMessage: 'Forecast unpublished'
      })
    };
  }, [intl, isPublishing]);

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleAction}
      screen={screen}
      title={text.title}
      subtitle={view.state.forecast?.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={text.description}
      loadingMessage={text.loadingMessage}
      successMessage={text.successMessage}
      errorMessage={view.state.errorUpdating?.message}
      dataTestId="publish-forecast-dialog"
    />
  );
};
